/**
 * Robot Framework Stream Parser
 * Parses streaming Robot Framework content and creates compartments
 */

class RobotFrameworkParser {
  constructor() {
    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PUBLIC API (used by App.js)
  // =================================================================

  processStreamContent(params) {
    const { content, section } = params;

    if (section !== "robotframework") {
      return {
        isSuccessful: false,
        content: []
      };
    }

    if (!content) {
      return {
        isSuccessful: false,
        content: []
      };
    }

    this.state.processingState = 'processing';
    this.state.buffer += content;

    try {
      this.parseRobotFrameworkTests(this.state.buffer);

      if (this.state.compartments.length > 0) {
        this.state.processingState = 'completed';
        return {
          isSuccessful: true,
          content: this.getValidCompartments()
        };
      }

      this.state.processingState = 'idle';
      return {
        isSuccessful: false,
        content: []
      };

    } catch (error) {
      console.error('Robot Framework Parser: Error processing content:', error);
      this.state.processingState = 'error';
      return {
        isSuccessful: false,
        content: []
      };
    }
  }

  resetState() {
    console.log('🔄 Robot Framework Parser: Resetting state');

    this.state = {
      compartments: [],
      buffer: "",
      processingState: 'idle'
    };
  }

  // =================================================================
  // PARSING LOGIC
  // =================================================================

  parseRobotFrameworkTests(buffer) {
    console.log('🤖 Robot Framework buffer received, length:', buffer.length);

    const beforeCount = this.state.compartments.length;
    const matches = [...buffer.matchAll(/New Robot Framework Test:/g)];
    const currentCount = matches.length;

    console.log(`🤖 Found ${currentCount} "New Robot Framework Test:" patterns, had ${beforeCount} compartments before`);

    if (currentCount > beforeCount) {
      // Create new compartments only for new matches
      for (let i = beforeCount; i < currentCount; i++) {
        this.createCompartment(i + 1);
      }
      this.updateCompartmentContent(buffer);
    } else if (this.state.compartments.length > 0) {
      // Stream to last compartment
      this.streamToLastCompartment(buffer);
    }
  }

  createCompartment(number) {
    const compartment = {
      id: `robotframework_${number}`,
      number: number,
      title: `Robot Framework Test ${number}`,
      content: "",
      isExpanded: true
    };

    this.state.compartments.push(compartment);
    console.log(`🆕 Robot Framework Parser: Compartment ${number} created`);
  }

  updateCompartmentContent(fullBuffer) {
    const sections = fullBuffer.split('New Robot Framework Test:');

    // Only process existing compartments, don't create new ones here
    sections.slice(1).forEach((section, index) => {
      if (index < this.state.compartments.length) {
        const existingCompartment = this.state.compartments[index];

        if (!this.isValidCompartment(existingCompartment)) {
          console.error(`❌ Invalid compartment at index ${index}:`, existingCompartment);
          return;
        }

        const lines = section.trim().split('\n');
        const title = (lines[0] && lines[0].trim()) || `Robot Framework Test ${index + 1}`;
        let cleanContent = lines.slice(1).join('\n').trim();

        // Stop at next test or end markers
        const nextTestIndex = cleanContent.indexOf('New Robot Framework Test:');
        if (nextTestIndex !== -1) {
          cleanContent = cleanContent.substring(0, nextTestIndex).trim();
        }
        cleanContent = cleanContent.replace(/ENDOFANA.*$/s, '').trim();

        const updatedCompartment = {
          ...existingCompartment,
          title: title,
          content: cleanContent
        };

        if (this.isValidCompartment(updatedCompartment)) {
          this.state.compartments[index] = updatedCompartment;
        }
      }
    });
  }

  streamToLastCompartment(buffer) {
    if (this.state.compartments.length === 0) return;

    const lastIndex = this.state.compartments.length - 1;
    const existingCompartment = this.state.compartments[lastIndex];

    if (!this.isValidCompartment(existingCompartment)) {
      console.error(`❌ Invalid last compartment at index ${lastIndex}:`, existingCompartment);
      return;
    }

    const sections = buffer.split('New Robot Framework Test:');

    if (sections.length > lastIndex + 1) {
      const lastSection = sections[lastIndex + 1];
      const lines = lastSection.trim().split('\n');
      const title = (lines[0] && lines[0].trim()) || existingCompartment.title;
      let cleanContent = lines.slice(1).join('\n').trim();

      // Stop at next test or end markers
      const nextTestIndex = cleanContent.indexOf('New Robot Framework Test:');
      if (nextTestIndex !== -1) {
        cleanContent = cleanContent.substring(0, nextTestIndex).trim();
      }
      cleanContent = cleanContent.replace(/ENDOFANA.*$/s, '').trim();

      const updatedCompartment = {
        ...existingCompartment,
        title: title,
        content: cleanContent
      };

      if (this.isValidCompartment(updatedCompartment)) {
        this.state.compartments[lastIndex] = updatedCompartment;
      }
    }
  }

  // =================================================================
  // UTILITY & VALIDATION
  // =================================================================

  isValidCompartment(compartment) {
    return compartment &&
           typeof compartment.id === 'string' &&
           compartment.id.length > 0 &&
           typeof compartment.number === 'number' &&
           (typeof compartment.title === 'string' || compartment.title === undefined);
  }

  getValidCompartments() {
    return this.state.compartments.filter(compartment => {
      const isValid = this.isValidCompartment(compartment);
      if (!isValid) {
        console.warn('Invalid compartment filtered out:', compartment);
      }
      return isValid;
    });
  }
}

export default RobotFrameworkParser;
